import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid2,
  Paper,
  Divider,
  Dialog,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormattedDate, FormattedTime, useIntl } from "react-intl";
import { parseISO } from "date-fns";
import { useSnackbar } from "notistack";

import RevaText from "components/common/RevaText";
import MatchScoreTable from "./MatchScoreTable";
import EditMatchModal from "../../Tournament/EditMatchModal";
import { formatMatchIdentity } from "components/common/matchIdentity";

const ScheduleByVenue = ({ matches, refresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [selectedMatch, setSelectedMatch] = useState(null);

  const handleMatchUpdated = () => {
    enqueueSnackbar(intl.formatMessage({ id: "Partido actualizado" }), {
      variant: "success",
    });
    setSelectedMatch(null);
    if (refresh) refresh();
  };
  // Filtrar solo partidos con sede y fecha
  const scheduledGames = useMemo(() => {
    return matches.filter((g) => g.is_scheduled);
  }, [matches]);

  // Agrupar por establecimiento y cancha
  const grouped = useMemo(() => {
    const result = {};

    scheduledGames.forEach((game) => {
      const estId = game.establishment.id;
      const spaceId = game.space.id;

      if (!result[estId]) {
        result[estId] = {
          name: game.establishment.name,
          spaces: {},
        };
      }

      if (!result[estId].spaces[spaceId]) {
        result[estId].spaces[spaceId] = {
          name: game.space.name,
          matches: [],
        };
      }

      result[estId].spaces[spaceId].matches.push(game);
    });

    return result;
  }, [scheduledGames]);

  return (
    <Box>
      {Object.entries(grouped).map(([estId, est]) => (
        <Accordion key={estId} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{est.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {Object.entries(est.spaces).map(([spaceId, space]) => (
              <Box key={spaceId} mb={2}>
                <RevaText variant="h6" sx={{ mb: 1 }}>
                  {space.name}
                </RevaText>
                <Grid2 container spacing={2}>
                  {space.matches
                    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                    .map((game) => (
                      <Grid2 size={{ xs: 12, sm: 6, lg: 4 }} key={game.id}>
                        <Paper
                          borderRadius={2}
                          sx={{ overflow: "hidden", cursor: "pointer", "&:hover": { boxShadow: 3 } }}
                          onClick={() => setSelectedMatch(game)}
                        >
                          <RevaText sx={{ p: 1, backgroundColor: "background.gray" }}>
                            {formatMatchIdentity(game, (id, values) => intl.formatMessage({ id }, values))}
                          </RevaText>
                          <MatchScoreTable match={game} />
                          <Box sx={{ p: 1, pl: 2 }}>
                            <RevaText variant="caption" color="text.secondary">
                              <FormattedDate
                                value={parseISO(game.scheduled_at)}
                                weekday="long"
                                day="2-digit"
                                month="long"
                              />
                              , <FormattedTime value={new Date(game.scheduled_at)} hour="2-digit" minute="2-digit" />
                            </RevaText>
                          </Box>
                        </Paper>
                      </Grid2>
                    ))}
                </Grid2>
                <Divider sx={{ my: 6 }} />
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
      <Dialog
        open={Boolean(selectedMatch)}
        onBackdropClick={() => setSelectedMatch(null)}
        disableAutoFocus
        fullWidth={false}
        maxWidth="md"
      >
        {selectedMatch && (
          <EditMatchModal
            matchId={selectedMatch.id}
            tabName="details"
            canEdit
            isBracketStage={selectedMatch.tournament?.state === "underway"}
            showScoreTab={Boolean(selectedMatch.participants?.[0]?.id && selectedMatch.participants?.[1]?.id)}
            onSubmitScore={handleMatchUpdated}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default ScheduleByVenue;
